import { dataPickerFormat } from '../../../../../../../components/date-picker';
import { MeasurementRange } from '../../../../../../../types/measurement';
import { convertDateToFormat, convertDateToString } from '../../../../../../../utils/helpers/date-convert';
import { ChartDataItem, RangeFromTo } from '../types';
import { currentFormat } from './helpers';

export const getDateRangeByPage = (activeMeasurementRange: MeasurementRange, page: number): RangeFromTo => {
  const now = new Date();
  const baseDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let fromOutput = new Date();
  let toOutput = new Date();

  switch (activeMeasurementRange) {
    case MeasurementRange.DayInHours: {
      const targetDate = new Date(baseDate);
      targetDate.setDate(targetDate.getDate() + page);

      const from = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 0, 0, 0, 0);

      const to = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }

    case MeasurementRange.WeekInDays: {
      const targetDate = new Date(baseDate);
      targetDate.setDate(targetDate.getDate() + page * 7);

      const dayOfWeek = targetDate.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      const from = new Date(
        targetDate.getFullYear(),
        targetDate.getMonth(),
        targetDate.getDate() - daysToSubtract,
        0,
        0,
        0,
        0,
      );

      const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6, 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }

    case MeasurementRange.MonthInDays: {
      const year = baseDate.getFullYear();
      const month = baseDate.getMonth() + page;

      const from = new Date(year, month, 1, 0, 0, 0, 0);
      const to = new Date(year, month + 1, 0, 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }

    case MeasurementRange.YearInMonths: {
      const year = baseDate.getFullYear() + page;

      const from = new Date(year, 0, 1, 0, 0, 0, 0);
      const to = new Date(year, 11, 31, 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }

    case MeasurementRange.TenYearsInYears: {
      const year = baseDate.getFullYear() + page * 10;

      const from = new Date(year - 9, 0, 1, 0, 0, 0, 0);
      const to = new Date(year, 11, 31, 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }
  }

  return {
    from: fromOutput.toISOString(),
    to: toOutput.toISOString(),
  };
};

export const getRangeLabel = (activeMeasurementRange: MeasurementRange, rangeFromTo: RangeFromTo) => {
  let range = '';
  const dateFormat = dataPickerFormat[activeMeasurementRange].format[0];

  switch (activeMeasurementRange) {
    case MeasurementRange.DayInHours: {
      const from = new Date(rangeFromTo.from);

      if (from) range = `${convertDateToFormat(from, dateFormat)}`;

      break;
    }
    case MeasurementRange.WeekInDays: {
      const from = new Date(rangeFromTo.from);
      const to = new Date(rangeFromTo.to);

      if (from && to) range = `${convertDateToFormat(from, dateFormat)} - ${convertDateToFormat(to, dateFormat)}`;

      break;
    }
    case MeasurementRange.MonthInDays: {
      const from = new Date(rangeFromTo.from);

      if (from) range = convertDateToFormat(from, dateFormat);

      break;
    }
    case MeasurementRange.YearInMonths: {
      const to = new Date(rangeFromTo.to);

      if (to) range = `${to.getFullYear()}`;

      break;
    }
    case MeasurementRange.TenYearsInYears: {
      const from = new Date(rangeFromTo.from);
      const to = new Date(rangeFromTo.to);

      const adjustedFrom = new Date(from);
      adjustedFrom.setDate(adjustedFrom.getDate() + 1);

      if (adjustedFrom && to)
        range = `${convertDateToFormat(adjustedFrom, dateFormat)} - ${convertDateToFormat(to, dateFormat)}`;
      break;
    }
  }

  return range;
};

const createChartDataItem = (date: Date, activeMeasurementRange: MeasurementRange): ChartDataItem => ({
  date: date.getTime(),
  time: convertDateToString(date.getTime(), currentFormat(false, activeMeasurementRange)),
  tooltipTime: convertDateToString(date.getTime(), currentFormat(true, activeMeasurementRange)),
  values: [],
});

const getDateIncrementFunction = (activeMeasurementRange: MeasurementRange) => {
  switch (activeMeasurementRange) {
    case MeasurementRange.DayInHours:
      return (date: Date) => date.setHours(date.getHours() + 1);
    case MeasurementRange.WeekInDays:
    case MeasurementRange.MonthInDays:
      return (date: Date) => date.setDate(date.getDate() + 1);
    case MeasurementRange.YearInMonths:
      return (date: Date) => date.setMonth(date.getMonth() + 1);
    case MeasurementRange.TenYearsInYears:
      return (date: Date) => date.setFullYear(date.getFullYear() + 1);
    default:
      return (date: Date) => date.setDate(date.getDate() + 1);
  }
};

export const generateEmptyData = (
  activeMeasurementRange: MeasurementRange,
  { from, to }: RangeFromTo,
): ChartDataItem[] => {
  const arr: ChartDataItem[] = [];
  let currentDate = new Date(from);
  const endDate = new Date(to);
  const incrementDate = getDateIncrementFunction(activeMeasurementRange);

  while (currentDate <= endDate) {
    arr.push(createChartDataItem(new Date(currentDate), activeMeasurementRange));
    incrementDate(currentDate);
  }

  return arr;
};
